import { create } from 'zustand';

import { api } from '../lib/api.js';
import { WEBSITE } from '../lib/edition.js';

/**
 * Prisma's mode for the chat: everyday, or Super Prisma when the account's
 * rank has it (website and desktop alike; the server checks limits on every
 * message). No model or provider is ever shown.
 */

export interface ModelChoice {
  source: 'own' | 'demo';
  provider: string;
  model: string;
}

export interface Choices {
  own: { provider: string; label: string; suggested: string[] }[];
  demo: {
    ok: boolean;
    reason: string | null;
    rank: { id: number; name: string; color: string } | null;
    models: { provider: string; label: string; model: string; isDefault: boolean }[];
  };
}

interface WebsiteModelState {
  choices: Choices | null;
  choice: ModelChoice | null;
  /** "Super Prisma": the rank's Claude model instead of its everyday one. */
  superPrisma: boolean;
  load: () => Promise<void>;
  pick: (choice: ModelChoice) => void;
  setSuper: (on: boolean) => void;
}

const STORAGE = 'prism.website.model';
const SUPER_STORAGE = 'prism.website.super';

/** Super Prisma runs on Claude. */
const isSuper = (m: { provider: string }) => m.provider === 'claudecli' || m.provider === 'anthropic';

/** Whether the account's rank offers Super Prisma. */
export function superAvailable(choices: Choices | null): boolean {
  return Boolean(choices?.demo.models.some(isSuper));
}

/** Prisma's model: everyday (the rank's default non-Claude demo model) or Super (its Claude one). */
function prismaChoice(choices: Choices, superOn: boolean): ModelChoice | null {
  const models = choices.demo.models;
  const everyday = models.filter((m) => !isSuper(m));
  const pick = superOn ? models.find(isSuper) : (everyday.find((m) => m.isDefault) ?? everyday[0] ?? models.find((m) => m.isDefault) ?? models[0]);
  return pick ? { source: 'demo', provider: pick.provider, model: pick.model } : null;
}

function rememberedSuper(): boolean {
  try {
    return localStorage.getItem(SUPER_STORAGE) === '1';
  } catch {
    return false;
  }
}

function remembered(): ModelChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE);
    return raw ? (JSON.parse(raw) as ModelChoice) : null;
  } catch {
    return null;
  }
}

export const useWebsiteModel = create<WebsiteModelState>((set, get) => ({
  choices: null,
  choice: WEBSITE ? remembered() : null,
  superPrisma: rememberedSuper(),
  load: async () => {
    const account = await api.get<{ choices: Choices | null }>(WEBSITE ? '/account' : '/prisma/modes');
    const choices = account.choices;
    if (!choices) return;
    // Only Prisma's own models: everyday, or Super when the rank has it and it is on.
    const superOn = get().superPrisma && superAvailable(choices);
    set({ choices, superPrisma: superOn, choice: prismaChoice(choices, superOn) });
  },
  setSuper: (on) => {
    try {
      localStorage.setItem(SUPER_STORAGE, on ? '1' : '0');
    } catch {
      // per-browser convenience only
    }
    const choices = get().choices;
    const superOn = on && superAvailable(choices);
    set({ superPrisma: superOn, choice: choices ? prismaChoice(choices, superOn) : get().choice });
  },
  pick: (choice) => {
    try {
      localStorage.setItem(STORAGE, JSON.stringify(choice));
    } catch {
      // per-browser convenience only
    }
    set({ choice });
  },
}));

/** Extra body fields for a chat message: Prisma's mode (everyday or Super). */
export function websiteChoice(): { use?: ModelChoice } {
  const choice = useWebsiteModel.getState().choice;
  return choice ? { use: choice } : {};
}
